import { Chip, Stack, Switch, Typography } from '@mui/material';
import type { CSSProperties, ReactNode } from 'react';

type Variant = 'blue' | 'green';

interface Props {
    enabled: boolean;
    label: string;
    description: string;
    statusText: string;
    onToggle(): void;
    disabled?: boolean;
    icon?: ReactNode;
    variant?: Variant;
    children?: ReactNode;
}

const variantStyles: Record<Variant, Pick<CSSProperties, 'background' | 'border'>> = {
    blue: {
        background: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)',
        border: '1px solid #bbdefb',
    },
    green: {
        background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
        border: '1px solid #c8e6c9',
    },
};

export default function ToggleSection({
    enabled,
    label,
    description,
    statusText,
    onToggle,
    disabled = false,
    icon,
    variant = 'blue',
    children,
}: Props) {
    return (
        <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{
                ...variantStyles[variant],
                p: '14px',
                borderRadius: '6px',
                mb: 2,
                flexWrap: 'wrap',
            }}
        >
            {icon && (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        background: '#4caf50',
                        color: 'white',
                    }}
                >
                    {icon}
                </Stack>
            )}
            <Switch checked={enabled} onChange={onToggle} disabled={disabled} />
            <Stack sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500} color="#202124">
                    {label}
                </Typography>
                <Typography variant="caption" color="#5f6368">
                    {description}
                </Typography>
            </Stack>
            <Chip
                label={statusText}
                size="small"
                sx={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    color: '#5f6368',
                    fontWeight: 500,
                }}
            />
            {children}
        </Stack>
    );
}
